import React, { useLayoutEffect, useEffect, useState } from 'react';
import styles from './ContextMenu.module.scss';
import { useContextMenuNavigation } from './useContextMenuNavigation';
import { blur, triggerFocus } from '../navigation/utils';
import { useContextMenu } from './useContextMenu';
import ContextMenuButton from './ContextMenuButton';
import { useContextMenuContext } from '../contexts/ContextMenuContext/ContextMenuContext';
import { useInputMethodContext } from '../contexts/InputMethodContext/InputMethodContext';
import { useEventListener } from '../hooks/useEventListener';
import { Keys } from '../utils/keys';
import { useAriaLive } from '../contexts/AriaLiveContext/AriaLiveContext';
import { getTranslation } from '../localizations/translations';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { logger } from '../../common/logger';

const ContextMenu: React.FC = () => {
  const {
    contextMenuSource,
    isContextMenuOpen: isOpen,
    closeContextMenu: close,
    title,
    info,
    labels,
  } = useContextMenuContext();
  const { data } = useContextMenu(contextMenuSource);
  const { focus, triggerNavigation } = useContextMenuNavigation(data);
  const { inputMethod } = useInputMethodContext();
  const isInputMethodKeyboard = inputMethod === 'keyboard';
  const { announceAssertive } = useAriaLive();
  const { languageCode } = useAppFeaturesContext();
  const [pressedButtonIndex, setPressedButtonIndex] = useState<
    undefined | number
  >(undefined);

  const clearPressedMenuButton = () => {
    setPressedButtonIndex(undefined);
    close();
  };

  useEffect(() => {
    if (!isOpen) return;
    setTimeout(
      () =>
        announceAssertive(
          getTranslation('common_accesibility_contextmenu_open', languageCode)
        ),
      1000
    );
  }, [isOpen, announceAssertive, languageCode]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    if (isInputMethodKeyboard) {
      triggerFocus(focus, true);
    } else {
      blur(focus);
    }
  }, [focus, isInputMethodKeyboard, isOpen]);

  const onKeyDownEvent = (e: any) => {
    if (!isInputMethodKeyboard) return;

    if (e.repeat) return;

    if (
      isOpen &&
      (e.keyCode === Keys.VK_SPACE || e.keyCode === Keys.VK_ENTER)
    ) {
      try {
        const pressedButtonIndex = parseInt(e.target.id.split('/')[2]);
        setPressedButtonIndex(pressedButtonIndex);
      } catch (e) {
        logger.error(
          { error: e },
          'Could not parse ContextMenuButton index from event.target.id'
        );
      }
      e.preventDefault();
      e.stopPropagation();
    }

    let isEventCaptured = true;

    if (e.keyCode === Keys.VK_UP || e.keyCode === Keys.VK_DOWN) {
      triggerNavigation(e);
    } else if ([Keys.VK_BACK, Keys.VK_BACKSPACE].includes(e.keyCode)) {
      close();
    } else {
      isEventCaptured = false;
    }

    if (isEventCaptured) {
      e.preventDefault();
    }
  };

  useEventListener('keydown', onKeyDownEvent, window);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.contextMenu__overlay}>
      <div className={styles.contextMenu}>
        <div className={styles.contextMenu__header}>
          <h3 className={styles.contextMenu__title}>{title}</h3>
          <p className={styles.contextMenu__info}>{info}</p>
          <p className={styles.contextMenu__labels}>{labels}</p>
        </div>
        <div className={styles.contextMenu__buttons}>
          {data.map((control, index) => (
            <ContextMenuButton
              id={`0/-4/${index}`}
              control={control}
              key={`${control.type}-${control.destination?.uri || control.operation?.uri || control.title}`}
              isPressed={pressedButtonIndex === index}
              clearPressed={clearPressedMenuButton}
              languageCode={languageCode}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContextMenu;
